import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { applyTheme, type Theme } from '@/lib/theme/applyTheme';

/**
 * Active color theme, persisted to localStorage so it survives a refresh.
 *
 * INFRASTRUCTURE ONLY: the dark visual design is not ready, so the default is
 * forced to `'light'` and no toggle UI is shipped yet. The store, the
 * `data-theme` mechanism, and the dark token slots (see `src/index.css`) are in
 * place so a settings toggle can be wired later by simply calling `setTheme`.
 *
 * The store self-applies: on rehydrate and on every change it writes the
 * `data-theme` attribute via {@link applyTheme}. An inline bootstrap script in
 * `index.html` paints the correct attribute before React mounts (no flash).
 */
interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'light',
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set((state) => ({ theme: state.theme === 'dark' ? 'light' : 'dark' })),
    }),
    {
      name: 'predictions.theme',
      onRehydrateStorage: () => (state) => {
        // Re-sync the DOM to the persisted value once it lands.
        if (state) applyTheme(state.theme);
      },
    },
  ),
);

// Apply the current value at module load (covers the no-stored-value default)
// and on every subsequent change, so the attribute always tracks the store.
applyTheme(useThemeStore.getState().theme);
useThemeStore.subscribe((state) => applyTheme(state.theme));

/** The active theme. */
export const useTheme = (): Theme => useThemeStore((s) => s.theme);

/** Set the theme explicitly (for a future settings toggle). */
export const useSetTheme = (): ((theme: Theme) => void) => useThemeStore((s) => s.setTheme);

/** Flip between light and dark. */
export const useToggleTheme = (): (() => void) => useThemeStore((s) => s.toggleTheme);

/** Imperative setter for non-React call sites. */
export const setThemeImperative = (theme: Theme): void => useThemeStore.getState().setTheme(theme);
